import useModalStore from "zustandStores/useModalStore";
import VideoPlayer, { VideoPlayerProps } from "./VideoPlayer";
import { modalStateValues, modalType } from "services/constants";
import { useEffect, useRef } from "react";

interface VideoPlayerInBackdropData extends VideoPlayerProps {}

/**
 * VideoPlayerInBackdrop - A modal component that displays a VideoPlayer in a fullscreen backdrop
 *
 * This component:
 * 1. Renders a VideoPlayer inside a modal backdrop
 * 2. Controls body scrolling (prevents scrolling when modal is open)
 * 3. Tracks video progress and reports it on close
 * 4. Handles closing the modal when clicking outside the video
 *
 * Usage:
 * This component is controlled via the modalStore:
 *
 * To open:
 * ```
 * setModalData({
 *   state: modalStateValues.open,
 *   type: modalType.VIDEO_MODAL_BACKDROP,
 *   data: {
 *     videoUrl: "/path/to/video.mp4",
 *     thumbnailUrl: "/path/to/thumbnail.jpg",
 *     // Other VideoPlayer props
 *   }
 * });
 * ```
 */
const VideoPlayerInBackdrop = () => {
  const { modalData, setModalData } = useModalStore(state => ({
    modalData: state.modalData,
    setModalData: state.setModalData
  }));
  const { type, state, data = {} } = modalData;

  // Use a ref to access the video DOM element
  // This allows calculating percentWatched when modal is closed
  const videoRef = useRef<HTMLVideoElement>(null);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (
      type === modalType.VIDEO_MODAL_BACKDROP &&
      state === modalStateValues.open
    ) {
      // Disable scrolling on body
      document.body.style.overflow = "hidden";
    } else {
      // Re-enable scrolling when modal is closed
      document.body.style.overflow = "auto";
    }

    // Cleanup when component unmounts
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [type, state]);

  // Don't render anything if modal type isn't video or it's closed
  if (type !== modalType.VIDEO_MODAL_BACKDROP) {
    return null;
  }
  if (state === modalStateValues.CLOSE) {
    return null;
  }

  // Extract video props from modal data
  const {
    videoUrl,
    controls = true,
    autoPlay = false,
    muted = false,
    fullScreen = false,
    thumbnailUrl,
    onClose,
    onPlay,
    onPause,
    onSeek,
    onEnded,
    onInitialPlay,
    showCloseButton = false
  } = data as VideoPlayerInBackdropData;

  /**
   * Handle closing the modal
   * Calls the onClose callback with percentWatched analytics
   * Then updates the modalStore to close the modal
   */
  const handleClose = (data: { percentWatched: number }) => {
    if (onClose) {
      onClose(data);
    }
    setModalData({
      state: modalStateValues.CLOSE,
      type: modalType.VIDEO_MODAL_BACKDROP,
      data: {}
    });
  };

  /**
   * Handle clicks outside the video player
   * Calculates the current percentWatched and calls handleClose
   */
  const handleClickOutside = () => {
    const video = videoRef.current;

    // Calculate percentWatched if video ref is available
    let percentWatched = 0;
    if (video && video.duration && video.currentTime) {
      percentWatched = Math.floor((video.currentTime / video.duration) * 100);
    }

    handleClose({ percentWatched });
  };

  return (
    <div
      className={
        "fixed inset-0 bg-black/80 z-50 flex justify-center items-center"
      }
      onClick={handleClickOutside}
    >
      <div className={"sm:w-4/5"} onClick={e => e.stopPropagation()}>
        <div className="aspect-video relative">
          {/* 
            Pass the ref to VideoPlayer to access the video element
            This allows tracking progress and accessing video properties
          */}
          <VideoPlayer
            ref={videoRef}
            videoUrl={videoUrl}
            controls={controls}
            autoPlay={autoPlay}
            muted={muted}
            fullScreen={fullScreen}
            thumbnailUrl={thumbnailUrl}
            onClose={handleClose}
            showCloseButton={showCloseButton}
            onPlay={onPlay}
            onPause={onPause}
            onSeek={onSeek}
            onEnded={onEnded}
            onInitialPlay={onInitialPlay}
          />
        </div>
      </div>
    </div>
  );
};

export default VideoPlayerInBackdrop;
